<template>
    <q-header elevated class="app-header-quasar bg-white text-dark">
    <q-toolbar class="q-py-sm">
      <!-- Mobile sidebar toggle -->
      <q-btn
        v-if="$q.platform.is.mobile"
        flat
        dense
        round
        icon="menu"
        color="primary"
        @click="toggleSidebar"
        aria-label="Toggle sidebar"
      />      <!-- Logo (centered on mobile) -->
      <div v-if="$q.platform.is.mobile" class="q-toolbar-title flex flex-center q-pa-none">
        <router-link to="/" class="logo-container flex no-wrap items-center justify-center">
          <q-img
            v-if="!logoError"
            :src="logoSrc"
            alt="CouponLa"
            spinner-color="primary"
            class="logo"
            @error="onLogoError"
            width="48px"
            height="48px"
            contain
          />
          <span v-else class="text-weight-bold text-gradient">CouponLa</span>
        </router-link>
      </div>
      
      <!-- Mobile search icon -->
      <q-btn
        v-if="$q.platform.is.mobile"
        flat
        dense
        round
        icon="search"
        color="primary"
        @click="openMobileSearch"
        aria-label="Search"
      />
      <router-link v-else to="/" class="logo-container flex no-wrap items-center">
        <q-img
          v-if="!logoError"
          :src="logoSrc"
          alt="CouponLa"
          spinner-color="primary"
          class="logo q-mr-sm"
          @error="onLogoError"
          width="60px"
          height="60px"
          contain
        />
        <span v-else class="text-weight-bold text-gradient">CouponLa</span>
      </router-link>

      <q-space v-if="!$q.platform.is.mobile" />

      <!-- Desktop search form only -->
      <q-form 
        v-if="!$q.platform.is.mobile"
        @submit="handleSearch" 
        class="search-form q-ml-md q-mr-sm"
        :class="{ 'full-width': $q.platform.is.mobile }"
      >
        <q-input
          v-model="searchQuery"
          filled
          rounded
          placeholder="Search coupons, stores, or brands..."
          class="search-input"
          bg-color="white"
          :dense="$q.platform.is.mobile"
        >
          <template v-slot:prepend>
            <q-icon name="search" color="grey-7" />
          </template>
          <template v-slot:append>
            <q-btn
              round
              dense
              flat
              color="amber"
              icon="search"
              type="submit"
            />
          </template>
        </q-input>
      </q-form>
      <div v-if="!$q.platform.is.mobile" class="q-mr-md"></div> <!-- Reduced space to shift search left -->

      <!-- Desktop Navigation -->
      <div v-if="!$q.platform.is.mobile" class="desktop-nav">
        <!-- Categories Dropdown -->
        <q-btn-dropdown
          flat
          no-caps
          label="Categories"
          icon="grid_view"
          color="dark"
          class="q-mr-sm"
        >
          <q-list>
            <q-item
              v-for="category in categories.slice(0, 10)"
              :key="category.id"
              clickable
              v-close-popup
              :to="{ name: 'Category', params: { slug: category.url_slug } }"
            >
              <q-item-section>{{ category.name }}</q-item-section>
            </q-item>
            
            <q-separator />
            
            <q-item clickable v-close-popup to="/categories">
              <q-item-section>All Categories</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>

        <!-- Stores Dropdown -->
        <q-btn-dropdown
          flat
          no-caps
          label="Stores"
          icon="storefront"
          color="dark"
          class="q-mr-sm"
        >
          <q-list>
            <q-item
              v-for="store in featuredStores"
              :key="store.id"
              clickable
              v-close-popup
              :to="{ name: 'Store', params: { slug: store.url_slug } }"
            >
              <q-item-section>{{ store.name }}</q-item-section>
            </q-item>
            
            <q-separator />
            
            <q-item clickable v-close-popup to="/stores">
              <q-item-section>All Stores</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>        <!-- Deals Link -->
        <q-btn
          flat
          no-caps
          to="/deals"
          label="Deals"
          icon="paid"
          color="dark"
          class="q-mr-sm"
        />

        <!-- Coupons Link -->
        <q-btn
          flat
          no-caps
          to="/coupons"
          label="Coupons"
          icon="local_offer"
          color="dark"
          class="q-mr-sm"
        />

        <!-- About Link -->
        <q-btn
          flat
          no-caps
          to="/about"
          label="About"
          icon="info"
          color="dark"
        />
      </div>

      <!-- Mobile menu button -->
      <q-btn
        v-if="$q.platform.is.mobile"
        flat
        dense
        round
        icon="more_vert"
        color="primary"
        class="q-ml-sm"
      >
        <q-menu>
          <q-list>
            <q-item clickable v-close-popup to="/categories">
              <q-item-section avatar>
                <q-icon name="grid_view" />
              </q-item-section>
              <q-item-section>Categories</q-item-section>
            </q-item>
              <q-item clickable v-close-popup to="/stores">
              <q-item-section avatar>
                <q-icon name="storefront" />
              </q-item-section>
              <q-item-section>Stores</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/deals">
              <q-item-section avatar>
                <q-icon name="paid" />
              </q-item-section>
              <q-item-section>Deals</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/coupons">
              <q-item-section avatar>
                <q-icon name="local_offer" />
              </q-item-section>
              <q-item-section>Coupons</q-item-section>
            </q-item>
            
            <q-item clickable v-close-popup to="/about">
              <q-item-section avatar>
                <q-icon name="info" />
              </q-item-section>
              <q-item-section>About</q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>    </q-toolbar>
    
    <!-- Mobile search dialog -->
    <q-dialog v-model="searchDialogOpen" position="top">
      <q-card class="search-dialog q-pa-md">
        <q-form @submit="handleSearch">
          <q-input
            v-model="searchQuery"
            filled
            bg-color="white"
            color="dark"
            placeholder="Search for stores or coupons"
            dense
            class="search-input"
            autofocus
          >
            <template v-slot:prepend>
              <q-icon name="search" color="grey-7" />
            </template>
            <template v-slot:append>
              <q-icon 
                v-if="searchQuery" 
                name="close" 
                color="grey" 
                @click.stop="searchQuery = ''" 
                class="cursor-pointer" 
              />
              <q-btn
                v-else
                dense
                flat
                icon="arrow_back"
                color="grey-7"
                @click="searchDialogOpen = false"
              />
            </template>
          </q-input>
        </q-form>
      </q-card>
    </q-dialog>
  </q-header>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { categoriesService, storesService } from '@/services/supabase';

// Emit events
const emit = defineEmits(['toggle-sidebar', 'search']);

// Setup
const router = useRouter();
const $q = useQuasar();
const searchQuery = ref('');
const categories = ref([]);
const featuredStores = ref([]);
const logoError = ref(false);
const logoSrc = ref('/images/logo.svg');
const searchDialogOpen = ref(false);

// Toggle sidebar
const toggleSidebar = () => {
  emit('toggle-sidebar');
};

// Open mobile search dialog
const openMobileSearch = () => {
  searchDialogOpen.value = true;
};

// Handle logo error
const onLogoError = () => {
  logoError.value = true;
  logoSrc.value = '';
};

// Handle search form submission
const handleSearch = () => {
  if (searchQuery.value.trim()) {
    // Emit search event for parent components that might be listening
    emit('search', searchQuery.value.trim());
    
    // Close dialog if open
    searchDialogOpen.value = false;
    
    // Navigate to search page with query
    router.push({
      name: 'Search',
      query: { q: searchQuery.value.trim() }
    });
  }
};

// Load categories and featured stores
const loadData = async () => {
  try {
    // Get categories
    const { data: categoriesData } = await categoriesService.getAll();
    if (categoriesData) {
      categories.value = categoriesData;
    }

    // Get featured stores
    const { data: storesData } = await storesService.getFeatured();
    if (storesData) {
      featuredStores.value = storesData.slice(0, 10); // Limit to 10
    }
  } catch (error) {
    console.error('Error loading header data:', error);
  }
};

// Load data on mount
onMounted(() => {
  loadData();
});
</script>

<style lang="scss" scoped>
.app-header-quasar {
  border-bottom-left-radius: 1.5rem;
  border-bottom-right-radius: 1.5rem;
  z-index: 1000;
  box-shadow: 0 2px 16px 0 rgba(0,0,0,0.04);
  transition: top 0.3s ease;

  @media (max-width: 767px) {
    border-radius: 0;
  }
}

.logo-container {
  text-decoration: none;
  color: inherit;
}

.logo {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(13,110,253,0.08);
}

.text-gradient {
  background: linear-gradient(90deg, $primary 0%, $amber 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.search-form {
  max-width: 250px; /* Reduced from 500px to half the size */
  width: 100%;
  margin-right: 24px; /* Add more margin to move it leftward */
}

.search-input {
  border-radius: 24px;
  box-shadow: 0 2px 12px rgba(13,110,253,0.07);
}

/* Mobile-specific styles */
.mobile-search-bar {
  background: linear-gradient(135deg, #3498db 0%, #1976d2 100%);
  
  .search-input {
    border-radius: 6px;
    overflow: hidden;
  }
}

/* Search dialog styles */
.search-dialog {
  width: 100%;
  border-radius: 0 0 16px 16px;
  background: linear-gradient(135deg, #3498db 0%, #1976d2 100%);
  
  .search-input {
    border-radius: 6px;
    overflow: hidden;
  }
}

.desktop-nav {
  min-width: 460px; /* Increased to accommodate the additional button */
  display: flex;
  align-items: center;
  
  .q-btn {
    font-weight: 500;
    padding: 8px 12px;
  }
}

@media (max-width: 1200px) {
  .desktop-nav {
    min-width: 440px; /* Slightly smaller on medium screens */
  }
}

@media (max-width: 991px) {
  .search-form {
    margin-bottom: 8px;
    margin-top: 8px;
    max-width: 200px; /* Slightly smaller on smaller screens */
    margin-right: 16px; /* Reduced margin on smaller screens */
  }
  
  .desktop-nav {
    min-width: 400px; /* Increased to accommodate the additional button */
  }
}

@media (max-width: 600px) {
  .search-form.full-width {
    max-width: 100%; /* Keep full width only on mobile */
    margin-right: 8px;
  }
}
</style>
